﻿import { useEffect, useState } from 'react';
import { Row, Col, ListGroup, Card, Button, Modal, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FiSave, FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';
import { useCrudTables } from '@/hooks/useCrudTables';
import { useCompanies } from '@/hooks/useCompanies';
import { useToast } from '@/components/common/Toast';
import PageHeader from '@/components/common/PageHeader';
import Spinner from '@/components/common/Spinner';

const RECORD_TYPES = [
  { key: 'luoghivisita', label: 'Luogo visita' },
  { key: 'motivivisite', label: 'Motivo della visita' },
  { key: 'commerciali', label: 'Presenti LSG COMM' },
  { key: 'qualita', label: 'Presenti LSG Qualità' },
  { key: 'rd', label: 'Presenti LSG RD' },
  { key: 'settoridivcont', label: 'Settore Div. Contenitori' },
  { key: 'settoriitc', label: 'Settore ITC' },
  { key: 'settorils', label: 'Settore LS' },
  { key: 'customergroup', label: 'Customer Group' },
  { key: 'channeldistibution', label: 'Distribution Channel' },
];

export default function ToolsPage() {
  const { t } = useTranslation();
  const { records, columns, primaryKeys, loading, fetchRecords, insertRecord, updateRecord } = useCrudTables();
  const { showToast } = useToast();
  const { companies, fetchCompanies } = useCompanies();
  const [activeType, setActiveType] = useState(RECORD_TYPES[0].key);
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState<Record<string, string> | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  useEffect(() => { fetchRecords(activeType); }, [activeType, fetchRecords]);

  const isPK = (col: string) => primaryKeys.some(pk => pk.field === col);
  const isAuto = (col: string) => primaryKeys.some(pk => pk.field === col && pk.autoincrement);
  const editableColumns = columns.filter(c => !isAuto(c));

  

  const openCreate = () => {
    setEditRow(null);
    setFormData({});
    setShowModal(true);
  };

  const openEdit = (rec: Record<string, string>) => {
    setEditRow(rec);
    setFormData({ ...rec });
    setShowModal(true);
  };

  const handleSave = async () => {
    const hasValues = editableColumns.some(c => (formData[c] || '').trim() !== '');
    if (!hasValues) { showToast('Attenzione', 'Compilare almeno un campo', 'warning'); return; }

    let ok: boolean;
    if (editRow) {
      ok = await updateRecord(activeType, formData);
    } else {
      ok = await insertRecord(activeType, formData);
    }

    if (ok) {
      showToast('OK', editRow ? 'Record aggiornato' : 'Record creato', 'success');
      setShowModal(false);
      fetchRecords(activeType);
    } else {
      showToast('Errore', 'Errore nel salvataggio', 'danger');
    }
  };

  const activeLabel = RECORD_TYPES.find(r => r.key === activeType)?.label || '';

  return (
    <>
      <PageHeader title={t('nav.tools')} />
      <div className="container py-3">
        <Row>
          <Col lg={3}>
            <ListGroup className="mb-3">
              {RECORD_TYPES.map(rt => (
                <ListGroup.Item key={rt.key} action active={rt.key === activeType}
                  onClick={() => setActiveType(rt.key)} style={{ fontSize: 12 }}>
                  {rt.label}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Col>
          <Col lg={9}>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0" style={{ fontSize: 13 }}>{activeLabel}</h6>
                <Button size="sm" variant="outline-success" onClick={openCreate}>
                  <FiPlus className="me-1" /> Crea
                </Button>
              </Card.Header>
              <Card.Body className="p-0">
                {loading ? <Spinner /> : (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0" style={{ fontSize: 12 }}>
                      <thead>
                        <tr>
                          {columns.map(col => (
                            <th key={col} className="px-2 py-2">{col}</th>
                          ))}
                          <th className="px-2 py-2" style={{ width: 60 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {records.length === 0 ? (
                          <tr><td colSpan={columns.length + 1} className="text-center text-muted py-3">Nessun record</td></tr>
                        ) : records.map((rec, i) => (
                          <tr key={i}>
                            {columns.map(col => (
                              <td key={col} className="px-2 py-2">
                              {col.toLowerCase() === 'attivo'
                                ? <span className={'badge ' + (rec[col] === 'S' ? 'bg-success' : 'bg-secondary')}>{rec[col] === 'S' ? 'Sì' : 'No'}</span>
                                : rec[col]}
                            </td>
                            ))}
                            <td className="px-2 py-2">
                              <Button size="sm" variant="outline-secondary" className="btn-icon"
                                onClick={() => openEdit(rec)} style={{ width: 28, height: 28 }}>
                                <FiEdit style={{ fontSize: 12 }} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Create / Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15 }}>
            {editRow ? 'Modifica' : 'Nuovo'} {activeLabel}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            {editableColumns.map(col => (
              <Col xs={12} key={col}>
                {col.toLowerCase() === 'attivo' ? (
                  <Form.Group>
                    <div className="form-check form-switch pt-2">
                      <input type="checkbox" className="form-check-input" id={'crud-' + col}
                        checked={(formData[col] || 'S') === 'S'}
                        onChange={e => setFormData(f => ({ ...f, [col]: e.target.checked ? 'S' : 'N' }))} />
                      <label className="form-check-label" style={{ fontSize: 11 }} htmlFor={'crud-' + col}>Attivo</label>
                    </div>
                  </Form.Group>
                ) : (
                  <Form.Group>
                    <Form.Label style={{ fontSize: 11 }}>{col}{isPK(col) && !editRow ? ' *' : ''}</Form.Label>
                    {col.toLowerCase() === 'codazienda' ? (
                      <Form.Select size="sm" value={formData[col] || ''}
                        onChange={e => setFormData(f => ({ ...f, [col]: e.target.value }))}
                        disabled={isPK(col) && !!editRow}>
                        <option value="">Seleziona...</option>
                        {companies.map(c => <option key={c.codice} value={c.codice}>{c.codice} - {c.descrizione}</option>)}
                      </Form.Select>
                    ) : (
                      <Form.Control size="sm" value={formData[col] || ''}
                        onChange={e => setFormData(f => ({ ...f, [col]: e.target.value }))}
                        disabled={isPK(col) && !!editRow} />
                    )}
                  </Form.Group>
                )}
              </Col>
            ))}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>{t('actions.cancel')}</Button>
          <Button variant="success" size="sm" onClick={handleSave}>
            <FiSave className="me-1" /> {t('actions.save')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}



